import { clampDateTo, type MeDto, PERIOD_PRESETS, type PeriodPreset, periodRange, type ReportMetaDto, datePl, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Button, Card, Group, Select, Tabs, TextInput } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { type Filters, toQuery } from './filters';
import { DashboardSection, FinanceSection, MarginSection, SalesSection } from './sections';


/** pole filtra: na telefonie po dwa w rzędzie, na komputerze w jednej linii */
const FIELD = { flex: '1 1 140px', maxWidth: 220 };

const TABS = ['pulpit', 'sprzedaz', 'finanse', 'marza'] as const;
type Tab = (typeof TABS)[number];

/** Parametry filtrów w adresie (link do zestawienia można wysłać dalej). */
function useFilters() {
  const [sp, setSp] = useSearchParams();
  const today = todayInWarsaw();
  const preset = (sp.get('okres') as PeriodPreset | null) ?? 'ten_rok';
  const custom = preset === 'wlasny';
  const range = custom ? { from: sp.get('od') ?? periodRange('ten_rok', today).from, to: sp.get('do') ?? today } : periodRange(preset in PERIOD_PRESETS ? (preset as Exclude<PeriodPreset, 'wlasny'>) : 'ten_rok', today);
  const f: Filters = {
    ...range,
    companyId: sp.get('firma') ?? undefined,
    tradeCode: sp.get('branza') ?? undefined,
    ownerId: sp.get('opiekun') ?? undefined,
  };
  const tab = (TABS as readonly string[]).includes(sp.get('zakladka') ?? '') ? (sp.get('zakladka') as Tab) : 'pulpit';
  const set = (patch: Record<string, string | null | undefined>) =>
    setSp(
      (prev) => {
        const n = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) v ? n.set(k, v) : n.delete(k);
        return n;
      },
      { replace: true },
    );
  return { f, preset, custom, tab, set };
}


/** Zestawienia właściciela: pulpit, sprzedaż, finanse, marża — wspólne filtry nad zakładkami, eksport do Excela. */
export function ReportsPage({ me }: { me: MeDto }) {
  const { f, preset, custom, tab, set } = useFilters();
  const meta = useQuery({ queryKey: ['reports-meta'], queryFn: () => api<ReportMetaDto>('/reports/meta'), staleTime: 300_000 });
  const m = meta.data;
  const badRange = f.from > f.to;

  if (!can(me, 'zestawienia.podglad'))
    return (
      <>
        <PageHeader title="Zestawienia" />
        <Alert color="gray">Nie masz uprawnienia do zestawień. Może je nadać właściciel w ustawieniach ról.</Alert>
      </>
    );

  const exportHref = tab !== 'pulpit' && !badRange ? `/api/reports/export/${tab}?${toQuery(f)}` : null;
  return (
    <>
      <PageHeader
        title="Zestawienia"
        description="Sprzedaż, finanse i marża szacunkowa. Wartości umów netto z pozycji podpisanych umów i aneksów; wpływy z zarejestrowanych wpłat; należności — stan na dziś."
        actions={
          exportHref && (
            <Button component="a" href={exportHref} variant="default" leftSection={<IconDownload size={16} />}>
              Eksport do Excela
            </Button>
          )
        }
      />
      <QueryState isLoading={meta.isLoading} error={meta.error}>
        {m && (
          <>
            <Card withBorder padding="sm" mb="md">
              <Group gap="sm" align="flex-end" wrap="wrap">
                {tab !== 'pulpit' && (
                  <Select
                    label="Okres"
                    style={FIELD}
                    allowDeselect={false}
                    value={preset}
                    onChange={(v) => set({ okres: v, ...(v === 'wlasny' ? { od: f.from, do: f.to } : { od: null, do: null }) })}
                    data={Object.entries(PERIOD_PRESETS).map(([value, label]) => ({ value, label }))}
                  />
                )}
                {tab !== 'pulpit' && custom && (
                  <>
                    <TextInput label="Od" type="date" style={FIELD} value={f.from} onChange={(e) => e.currentTarget.value && set({ od: e.currentTarget.value, do: clampDateTo(e.currentTarget.value, f.to) })} />
                    <TextInput label="Do" type="date" style={FIELD} value={f.to} min={f.from || undefined} onChange={(e) => e.currentTarget.value && set({ do: e.currentTarget.value })} error={badRange ? 'Przed początkiem' : undefined} />
                  </>
                )}
                {m.companies.length > 1 && (
                  <Select label="Firma" style={FIELD} placeholder="wszystkie" clearable value={f.companyId ?? null} onChange={(v) => set({ firma: v })} data={m.companies.map((c) => ({ value: c.id, label: c.name }))} />
                )}
                <Select label="Branża" style={FIELD} placeholder="wszystkie" clearable value={f.tradeCode ?? null} onChange={(v) => set({ branza: v })} data={m.trades.map((t) => ({ value: t.code, label: t.name }))} />
                {m.owners.length > 1 && (
                  <Select label="Opiekun" style={FIELD} placeholder="wszyscy" clearable searchable value={f.ownerId ?? null} onChange={(v) => set({ opiekun: v })} data={m.owners.map((o) => ({ value: o.id, label: o.name }))} />
                )}
                {(f.companyId || f.tradeCode || f.ownerId) && (
                  <Button variant="subtle" size="sm" onClick={() => set({ firma: null, branza: null, opiekun: null })}>
                    Wyczyść filtry
                  </Button>
                )}
              </Group>
            </Card>

            <Tabs value={tab} onChange={(v) => set({ zakladka: v === 'pulpit' ? null : v })} keepMounted={false} styles={{ tab: { flexShrink: 0 } }}>
              <Tabs.List mb="md" style={{ flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}>
                <Tabs.Tab value="pulpit">Pulpit</Tabs.Tab>
                <Tabs.Tab value="sprzedaz">Sprzedaż</Tabs.Tab>
                <Tabs.Tab value="finanse">Finanse</Tabs.Tab>
                {m.margin.percent && <Tabs.Tab value="marza">Marża</Tabs.Tab>}
              </Tabs.List>
              {badRange ? (
                <Alert color="orange">Początek okresu jest po jego końcu — popraw daty.</Alert>
              ) : (
                <>
                  <Tabs.Panel value="pulpit">
                    <DashboardSection f={f} onTab={(t) => set({ zakladka: t })} />
                  </Tabs.Panel>
                  <Tabs.Panel value="sprzedaz">
                    <SalesSection f={f} period={`${datePl(f.from)} – ${datePl(f.to)}`} />
                  </Tabs.Panel>
                  <Tabs.Panel value="finanse">
                    <FinanceSection f={f} period={`${datePl(f.from)} – ${datePl(f.to)}`} />
                  </Tabs.Panel>
                  {m.margin.percent && (
                    <Tabs.Panel value="marza">
                      <MarginSection f={f} period={`${datePl(f.from)} – ${datePl(f.to)}`} />
                    </Tabs.Panel>
                  )}
                </>
              )}
            </Tabs>
          </>
        )}
      </QueryState>
    </>
  );
}
